import type { LabResultCategoryGroup } from '@liza/fhir-utils';
import { useMemo } from 'react';
import { useCodeDisplay } from '../../hooks/useCodeDisplay';
import { Measurements, type MeasurementsProps } from '../care-plan/Measurements';
import { labCategoryExpectedMeasurements, labCategoryObservations } from './labResultCategoryRows';

export type LabCategoryMeasurementsProps = Omit<
  MeasurementsProps,
  'observations' | 'expectedMeasurements' | 'standalone' | 'rowOrder'
> & {
  category: LabResultCategoryGroup;
};

/**
 * One category's labs as `Measurements` rows, in panel order with undrawn members as empty rows. The
 * row inputs are memoized on the category, so a parent re-render (a keystroke in the search box above
 * the list) does not make every category re-group its readings.
 */
export const LabCategoryMeasurements = ({ category, ...props }: LabCategoryMeasurementsProps): React.ReactElement => {
  const { displayForResultCode } = useCodeDisplay();
  const observations = useMemo(() => labCategoryObservations(category), [category]);
  const expectedMeasurements = useMemo(
    () => labCategoryExpectedMeasurements(category, displayForResultCode),
    [category, displayForResultCode],
  );

  return (
    <Measurements
      standalone
      rowOrder="expected"
      observations={observations}
      expectedMeasurements={expectedMeasurements}
      {...props}
    />
  );
};
